import { createElement } from "react";
import { createBrowserRouter, RouteObject, RouterProvider, useRouteError } from "react-router-dom";
import {
  JournalsTitleRoute,
  JournalsVersionTitleRoute,
  JournalsRegistrationDetailsRoute,
  JournalsSectionFirstRoute,
  JournalsSectionFirstVersionRoute,
  JournalsSectionSecondRoute,
  JournalsSectionSecondVersionRoute,
  JournalsSectionFourthRoute,
  JournalsSectionFourthVersionRoute,
  JournalsSectionFifthRoute,
  JournalsSectionFifthVersionRoute,
  JournalsSectionSixRoute,
  JournalsSectionSixVersionRoute,
  JournalsVersionRoute,
  JournalsVersionsRoute,
} from "@/pages/journals";
import { JournalsSectionThirdRoute, JournalsSectionThirdVersionRoute } from "@/pages/journals/journals-section-third";
import {
  ObjectInfoRoute,
  ObjectJournalRoute,
  ObjectMembersRoute,
  ObjectSettingsRoute,
  ObjectEditRoute,
} from "@/pages/objects";
import { SupervisionAdminPageRoute } from "@/pages/supervision/supervision-admin-page";
import { SupervisionIntegrationPageRoute } from "@/pages/supervision/supervision-integration-page";
import { SupervisionNotificationsPageRoute } from "@/pages/supervision/supervision-notifications-page";
import { SupervisionProjectsPageRoute } from "@/pages/supervision/supervision-projects-page";
import { OrganizationPageRoute } from "@pages/organization/organization-page";
import { NotFoundPageRoute } from "@pages/page-not-found";
import { ProfilePageRoute } from "@pages/profile/profile-page";
import {
  ProjectDetailRoute,
  ProjectMembersRoute,
  ProjectObjectRoute,
  ProjectRelatedRoute,
  ProjectSettingsRoute,
  ProjectHistoryRoute,
} from "@pages/project";
import { ProjectDocstoreRoute } from "@pages/project/project-docstore";
import { ProjectRegistryRoute } from "@pages/project/project-registry";
import { ProjectRequisitesRoute } from "@pages/project/project-requisites";
import { ProjectStatisticsRoute } from "@pages/project/project-statistics";
import { ProjectsPageRoute } from "@pages/projects";
import { RecordsRegisterRoute } from "@pages/records-register";
import { getTestPageRoutes } from "@pages/test-page";
import { Navigate, routes, useAppRoutes } from "@shared/lib/react-router";
import {
  createAdminLayout,
  createGuestLayout,
  createUserLayout,
  createUnifiedLayout,
} from "../composition/layouts";
import {
  createProjectGeneralView,
  createProjectJournalsView,
  createProjectObjectsView,
  createProjectDocstoreView,
  createProjectSupervisionView,
} from "../composition/views";
import { createProjectWrapper, createUnifiedProjectWrapper } from "../composition/wrappers";

// https://github.com/remix-run/react-router/discussions/10166
function BubbleError() {
  const error = useRouteError();
  if (error) throw error;
  return null;
}

/**
 * Компонент для редиректа на 404 с учетом unified режима
 */
function NotFoundRedirect() {
  const appRoutes = useAppRoutes();
  // unified роуты не имеют page_not_found, используем обычные routes
  const notFoundPath = appRoutes === routes.unified 
    ? "/u/page-not-found/" 
    : routes.page_not_found();
  return <Navigate to={notFoundPath} />;
}

/**
 * Генератор роутов для проектов
 * @param isUnified - если true, то пути будут с префиксом /u/
 * @returns Набор роутов для createProjectWrapper или createUnifiedProjectWrapper
 */
function createProjectRoutes(isUnified = false): RouteObject[] {
  // Для unified режима заменяем пути на /u/projects/...
  const patchRoute = (route: RouteObject): RouteObject => {
    if (!isUnified || !route.path) return route;
    
    const patchedRoute: RouteObject = {
      ...route,
      path: route.path.replace(/^\/projects\//, '/u/projects/'),
    };
    
    if (route.children) {
      patchedRoute.children = route.children.map(patchRoute);
    }
    
    return patchedRoute;
  };

  const baseRoutes = [
    createProjectGeneralView(
      ProjectSettingsRoute(),
      ProjectMembersRoute(),
      ProjectDetailRoute(),
      ProjectObjectRoute(),
      ProjectRelatedRoute(),
      ProjectHistoryRoute(),
      ProjectRequisitesRoute(),
      ProjectStatisticsRoute(),
      ProjectRegistryRoute(),
    ),
    RecordsRegisterRoute(),
    createProjectDocstoreView(ProjectDocstoreRoute()),
    createProjectSupervisionView(
      SupervisionProjectsPageRoute(),
      SupervisionNotificationsPageRoute(),
      SupervisionIntegrationPageRoute(),
      SupervisionAdminPageRoute(),
    ),
    createProjectObjectsView(
      ObjectInfoRoute(),
      ObjectMembersRoute(),
      ObjectJournalRoute(),
      ObjectSettingsRoute(),
      ObjectEditRoute(),
    ),
    createProjectJournalsView(
      JournalsTitleRoute(),
      JournalsVersionTitleRoute(),
      JournalsRegistrationDetailsRoute(),
      JournalsSectionFirstRoute(),
      JournalsSectionFirstVersionRoute(),
      JournalsSectionSecondRoute(),
      JournalsSectionSecondVersionRoute(),
      JournalsSectionThirdRoute(),
      JournalsSectionThirdVersionRoute(),
      JournalsSectionFourthRoute(),
      JournalsSectionFourthVersionRoute(),
      JournalsSectionFifthRoute(),
      JournalsSectionFifthVersionRoute(),
      JournalsSectionSixRoute(),
      JournalsSectionSixVersionRoute(),
    ),
    JournalsVersionRoute(),
    JournalsVersionsRoute(),
  ];

  return isUnified ? baseRoutes.map(patchRoute) : baseRoutes;
}

const router = createBrowserRouter([
  {
    errorElement: createElement(BubbleError),
    children: [
      createUserLayout(
        ...getTestPageRoutes(),
        ProjectsPageRoute(),
        OrganizationPageRoute(),
        ProfilePageRoute(),
        createProjectWrapper(...createProjectRoutes(false)),
        // TODO:написать navigate конфиг, чтобы нижнее выражение привести к исполняемой функции
        { path: "/", element: <Navigate to={routes.projects()} /> },
      ),
      // Unified layout for iframe embedding (without header, footer, and sidebar)
      createUnifiedLayout(
        createUnifiedProjectWrapper(...createProjectRoutes(true)),
        { ...NotFoundPageRoute(), path: "/u/page-not-found/" },
      ),
      createGuestLayout(NotFoundPageRoute()),
      createAdminLayout(),
      { path: "*", element: createElement(NotFoundRedirect) },
    ],
  },
]);

export function BrowserRouter() {
  return <RouterProvider router={router} />;
}
